/**
 * GlobalSearch — Spotlight-style search dropdown for the topbar.
 *
 * Searches across all public platform content:
 *   Projects, Communities, Users (if isSearchable), Chronicles (published), Knowledge (published).
 *
 * Data source: client-side filter against already-cached frontend data.
 * Keyboard: ⌘K / Ctrl+K to focus, ↑↓ to navigate, Enter to open, Escape to close.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ChronicleSummary } from '../../api/chronicles';
import { fetchChronicles } from '../../api/chronicles';
import { mediaSrc } from '../../api/mediaUrl';
import type { BlogArticleSummary } from '../../api/blogArticles';
import { fetchArticlesByType } from '../../api/blogArticles';
import type { MiniProject } from '../../api/miniProjects';
import { MINI_LIFECYCLE_TO_PROJECT_STATUS } from '../../Data/MiniProjectConfig/miniProjectLifecycle';
import type { SearchBarMode } from '../../state/searchBarPreference';
import styles from './GlobalSearch.module.css';
import { isWorkshop } from '../../Data/Community/workshop';

// ── Constants ───────────────────────────────────────────────────
const MIN_QUERY_LENGTH = 2;
// DEV-888 follow-up: bumped from 3 to 10. The previous cap dropped legitimate
// results when many entities matched a generic term (e.g. "rotary" matches
// 91 clubs by entity_type — the org root itself was being squeezed out).
const MAX_PER_CATEGORY = 10;

// ── Search result types ─────────────────────────────────────────
export type SearchResultType =
  | 'project'
  | 'mini_project'
  | 'community'
  | 'user'
  | 'chronicle'
  | 'knowledge';

export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  subtitle: string;
  route: string;
  /** Optional category/status badge text */
  badge?: string;
  /** Optional image URL (avatar, hero, flag emoji) */
  image?: string;
}

// ── Props ───────────────────────────────────────────────────────
export interface GlobalSearchProps {
  /** Seed-shape projects from useApiProjects (project_GUID, project_name, etc.) */
  projects: Record<string, any>[];
  /**
   * V2-derived set of project GUIDs the current user can view. NULL while
   * V2 capabilities are still loading (the cold-start period) — in that
   * window we show all projects matching the query (same conservative
   * "show, don't blank" posture MyProjects uses). Once V2 loads, the set
   * restricts project results to authorized projects only.
   *
   * Per DEV-1050: backend GET /api/projects returns everything; this
   * client-side filter is the canonical enforcement point for Full Project
   * read access. See DEV-1071 for the backend-side ACL hardening project.
   */
  visibleProjectGuids?: Set<string> | null;
  /** Mini-Projects from useApiMiniProjects. Backend already filters by
   *  visibility so the array contains only MPs this user can read. */
  miniProjects?: MiniProject[];
  /** Community entities from useApiCommunityData (geographic: geo_country, geo_state, etc.) */
  communities: Array<{
    entity_guid: string;
    name: string;
    entity_type: string;
    visibility: string;
    is_active: boolean;
    /** DEV-888 — optional URL slug. When set on both root and sub, search builds slug URLs. */
    slug?: string | null;
    /**
     * DEV-1483 / DEV-961 / ADR-006 — set ⇒ this is a user's personal workshop,
     * which is NEVER discoverable (see isWorkshop). The field was already on the
     * wire and in the seed shape; the type just never declared it, which is part
     * of why the search filter was missed.
     */
    personal_owner_user_guid?: string | null;
  }>;
  /**
   * Custom entities from useApiCommunityData (org hierarchies: rotary_club, rotary_district, etc.).
   * Shape differs from community entities — no `visibility` column; visibility is implicit on
   * is_active. Surfaced through search alongside geographic communities so org-internal entities
   * are discoverable today (DEV-881).
   */
  customEntities?: Array<{ entity_guid: string; name: string; entity_type: string; is_active: boolean }>;
  /**
   * Community relationships (parent/child edges). DEV-888: used to walk to the
   * org root when building slug-based URLs for sub-communities. DEV-890: also
   * used to walk ancestors when checking if the caller is an admin somewhere
   * in the chain (e.g., Rotary admin can see private Rotary clubs).
   */
  relationships?: Array<{ parent_entity_guid: string; child_entity_guid: string; relationship_type: string }>;
  /** All security users from useApiSecurityData (seed shape: snake_case keys) */
  users: Record<string, any>[];
  /** Current user GUID — excluded from search results */
  currentUserGuid: string;
  /**
   * DEV-890. Community memberships from apiCommunityMemberships. Used to admit
   * private communities into search results when the caller is a member.
   */
  communityMemberships?: Array<{ user_guid: string; community_entity_guid: string; status?: string }>;
  /**
   * DEV-890. Community role assignments from apiCommunityRoles. Used to admit
   * private communities when the caller has admin/owner role on the community
   * itself OR on any ancestor (org admin cascading through tree).
   */
  communityRoles?: Array<{ user_guid: string; community_entity_guid: string; role: string; is_active?: boolean }>;
  /**
   * DEV-1389 — topbar display mode. 'bar' (default) renders the persistent
   * search field; 'icon' renders a collapsed magnifier that animates open on
   * click / ⌘K. Sourced from the per-user App Settings preference.
   */
  mode?: SearchBarMode;
}

// ── Helpers ─────────────────────────────────────────────────────

/**
 * Multi-term search: ALL whitespace-separated terms must appear
 * somewhere across the combined searchable fields. E.g. "coral belize"
 * matches an item whose name contains "coral" and whose community contains "Belize".
 */
function matchesQuery(fields: (string | null | undefined)[], terms: string[]): boolean {
  // Concatenate all fields into a single searchable string
  const combined = fields.filter(Boolean).join(' ').toLowerCase();
  return terms.every((term) => combined.includes(term));
}

function buildProjectResults(
  projects: Record<string, any>[],
  terms: string[],
  visibleProjectGuids: Set<string> | null | undefined,
): SearchResult[] {
  const results: SearchResult[] = [];
  for (const p of projects) {
    // Visibility filter — when the V2 cache has resolved (set is non-null),
    // restrict to projects the user can view. Null = V2 still loading
    // (cold-start) → don't filter, matching MyProjects' conservative
    // posture. See DEV-1071 for the backend-side hardening project.
    if (visibleProjectGuids != null) {
      const guidUpper = String(p.project_GUID || '').toUpperCase();
      if (!visibleProjectGuids.has(guidUpper)) continue;
    }
    if (matchesQuery([p.project_name, p.project_category, p.Community, p.description], terms)) {
      results.push({
        type: 'project',
        id: p.project_GUID,
        title: p.project_name || 'Untitled Project',
        subtitle: [p.project_category, p.Community].filter(Boolean).join(' · '),
        route: `/projects/${p.project_GUID}`,
        badge: p.project_status || undefined,
      });
      if (results.length >= MAX_PER_CATEGORY) break;
    }
  }
  return results;
}

/** User-facing lifecycle labels for Mini-Project status badges.
 *  DEV-1271: consolidated into the shared lifecycle map. */
const MP_LIFECYCLE_LABEL = MINI_LIFECYCLE_TO_PROJECT_STATUS;

function buildMiniProjectResults(
  miniProjects: MiniProject[],
  terms: string[],
  communityNameByGuid: Map<string, string>,
): SearchResult[] {
  const results: SearchResult[] = [];
  for (const mp of miniProjects) {
    // Exclude archived MPs from search results — same UX principle as
    // archived items being hidden from default lists per migration 163.
    if (mp.archivedAt) continue;
    const ownerCommunityName = communityNameByGuid.get(
      String(mp.ownerCommunityGuid || '').toUpperCase(),
    ) || '';
    if (matchesQuery(
      [mp.title, mp.shortDescription, mp.description, mp.categoryKey, ownerCommunityName],
      terms,
    )) {
      results.push({
        type: 'mini_project',
        id: mp.miniProjectGuid,
        title: mp.title || 'Untitled Mini-Project',
        subtitle: [mp.categoryKey, ownerCommunityName].filter(Boolean).join(' · '),
        // ProjectDispatcher at /projects/:id handles Mini-Project routing.
        route: `/projects/${mp.miniProjectGuid}`,
        badge: MP_LIFECYCLE_LABEL[mp.status] || mp.status || undefined,
      });
      if (results.length >= MAX_PER_CATEGORY) break;
    }
  }
  return results;
}

function buildCommunityResults(
  communities: GlobalSearchProps['communities'],
  customEntities: NonNullable<GlobalSearchProps['customEntities']>,
  relationships: NonNullable<GlobalSearchProps['relationships']>,
  terms: string[],
  visibilityCtx: {
    isSysAdmin: boolean;
    memberOfGuids: Set<string>;
    adminOfGuids: Set<string>;
  },
): SearchResult[] {
  // ── Build relationship lookups ────────────────────────────
  // Used to determine if an entity is top-level (no parent) and to walk to
  // the org root for slug URL construction.
  const norm = (g: string) => String(g || '').toUpperCase();
  const parentByChild = new Map<string, string>();
  for (const r of relationships) {
    if (r.relationship_type === 'parent_of' || r.relationship_type === 'contains') {
      parentByChild.set(norm(r.child_entity_guid), norm(r.parent_entity_guid));
    }
  }
  const entityByGuid = new Map<string, GlobalSearchProps['communities'][number]>();
  for (const e of communities) entityByGuid.set(norm(e.entity_guid), e);

  /** Walk to the topmost ancestor (the org or geo root). Returns the root entity. */
  function walkToRoot(entityGuid: string): GlobalSearchProps['communities'][number] | undefined {
    let cur = norm(entityGuid);
    let safety = 0;
    while (parentByChild.has(cur) && safety++ < 12) {
      cur = parentByChild.get(cur)!;
    }
    return entityByGuid.get(cur);
  }

  /** Build the canonical route for an entity — slug URL when possible, GUID otherwise. */
  function routeFor(entity: { entity_guid: string; slug?: string | null }): string {
    const isTopLevel = !parentByChild.has(norm(entity.entity_guid));
    if (entity.slug) {
      if (isTopLevel) return `/${entity.slug}`;
      const root = walkToRoot(entity.entity_guid);
      if (root?.slug) return `/${root.slug}/${entity.slug}`;
    }
    return `/communities/${entity.entity_guid}`;
  }

  // ── Score each candidate match ────────────────────────────
  // Higher score = higher in results. Ranking dimensions:
  //   +100  exact name match (case-insensitive)
  //   +60   name starts with the first search term
  //   +30   name contains the first search term
  //   +10   entity_type contains a term (entity_type-only matches rank lower
  //         than name-based matches so "rotary" doesn't drown the org root in
  //         91 club entity_type hits)
  //   +20   bonus for top-level entities (org roots) — promote them above
  //         their own descendants
  //   +5    bonus for active community pages (some entities are inactive
  //         "location only" labels — rank below active pages)
  type Scored = { result: SearchResult; score: number };
  const firstTerm = terms[0] || '';
  const scoreEntity = (
    entity: { name: string; entity_type: string; entity_guid: string; slug?: string | null },
    isTopLevel: boolean,
    isActive: boolean,
  ): number => {
    const nameLower = (entity.name || '').toLowerCase();
    const typeLower = (entity.entity_type || '').toLowerCase();
    let score = 0;
    if (nameLower === firstTerm) score += 100;
    else if (nameLower.startsWith(firstTerm)) score += 60;
    else if (nameLower.includes(firstTerm)) score += 30;
    else if (typeLower.includes(firstTerm)) score += 10;
    if (isTopLevel) score += 20;
    if (isActive) score += 5;
    return score;
  };

  const scored: Scored[] = [];

  // DEV-890. Visibility tier check. A private community is admitted to the
  // search results only when the caller is in one of the eligible tiers:
  //   - sysadmin (always)
  //   - member of this specific community
  //   - admin/owner on this community OR any ancestor (org admin cascades down)
  // Public communities pass through unconditionally (subject to is_active).
  function callerCanSeePrivate(entityGuid: string): boolean {
    if (visibilityCtx.isSysAdmin) return true;
    const targetNorm = norm(entityGuid);
    if (visibilityCtx.memberOfGuids.has(targetNorm)) return true;
    // Walk self + ancestors looking for an admin grant.
    let cur: string | undefined = targetNorm;
    let safety = 0;
    while (cur && safety++ < 12) {
      if (visibilityCtx.adminOfGuids.has(cur)) return true;
      cur = parentByChild.get(cur);
    }
    return false;
  }

  // Geographic + community-tree entities — gated on is_active always; private
  // visibility gated by the tier check above.
  for (const c of communities) {
    // DEV-1483 — workshops are NEVER discoverable, and this exclusion is
    // CATEGORICAL: it fires for the owner and for sysadmins too. Readable ≠
    // discoverable. A workshop's contents are already privacy-gated (that part
    // works); what leaked here was REACH — the workshop turning up as a findable
    // thing. Your workshop is reachable from your own spaces; search is not its
    // door. Deliberately checked BEFORE the privacy/visibility gate below,
    // because this is not a permission question and must not be satisfiable by
    // having permission.
    if (isWorkshop(c)) continue;
    if (!c.is_active) continue;
    if (c.visibility === 'private' && !callerCanSeePrivate(c.entity_guid)) continue;
    if (!matchesQuery([c.name, c.entity_type], terms)) continue;
    const typeLabel = (c.entity_type || '').replace(/^geo_/, '').replace(/^org_/, '').replace(/_/g, ' ');
    const isTopLevel = !parentByChild.has(norm(c.entity_guid));
    scored.push({
      result: {
        type: 'community',
        id: c.entity_guid,
        title: c.name,
        subtitle: typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1),
        route: routeFor(c),
      },
      score: scoreEntity(c, isTopLevel, c.is_active),
    });
  }

  // DEV-889 (2026-05-06): customEntities iteration removed. Migration 124
  // lifted every custom_entities row into community_entities, so iterating
  // both sources produced duplicate search results (every Rotary club showing
  // up twice). The customEntities prop is preserved for now to avoid a
  // breaking signature change, but its values are intentionally ignored.
  // Drop the prop entirely after the static frontend customEntities.ts file
  // and the legacy custom_entities DB table are retired (filed separately).
  void customEntities;

  // Sort by score desc, then by name asc for stable ordering
  scored.sort((a, b) => b.score - a.score || a.result.title.localeCompare(b.result.title));
  return scored.slice(0, MAX_PER_CATEGORY).map((s) => s.result);
}

function buildUserResults(
  users: Record<string, any>[],
  terms: string[],
  currentUserGuid: string,
): SearchResult[] {
  const results: SearchResult[] = [];
  for (const u of users) {
    // Seed-shape uses snake_case keys (is_active, user_guid, display_name, etc.)
    if (!u.is_active) continue;
    // TODO: Re-enable once backend is_searchable column is confirmed deployed:
    // if (u.is_searchable === false) continue;
    const guid = String(u.user_guid || '');
    if (guid.toUpperCase() === currentUserGuid.toUpperCase()) continue;
    if (matchesQuery([u.display_name, u.name, u.screen_name], terms)) {
      results.push({
        type: 'user',
        id: guid,
        title: u.display_name || u.name,
        subtitle: u.screen_name ? `@${u.screen_name}` : '',
        route: `/profile/${guid}`,
        image: u.profile_image_url || undefined,
      });
      if (results.length >= MAX_PER_CATEGORY) break;
    }
  }
  return results;
}

function buildChronicleResults(chronicles: ChronicleSummary[], terms: string[]): SearchResult[] {
  if (!Array.isArray(chronicles)) return [];
  const results: SearchResult[] = [];
  for (const c of chronicles) {
    if (c.status !== 'published') continue;
    if (matchesQuery([c.title, c.categoryKey, c.initiatorDisplayName], terms)) {
      results.push({
        type: 'chronicle',
        id: c.chronicleGuid,
        title: c.title || 'Untitled Chronicle',
        subtitle: [c.categoryKey?.replace(/_/g, ' '), c.initiatorDisplayName].filter(Boolean).join(' · '),
        route: `/chronicles/${c.chronicleGuid}/view`,
        badge: c.categoryKey || undefined,
      });
      if (results.length >= MAX_PER_CATEGORY) break;
    }
  }
  return results;
}

function buildKnowledgeResults(articles: BlogArticleSummary[], terms: string[]): SearchResult[] {
  if (!Array.isArray(articles)) return [];
  const results: SearchResult[] = [];
  for (const a of articles) {
    if (a.status !== 'published') continue;
    if (matchesQuery([a.title, a.blogType, a.authorDisplayName, a.category], terms)) {
      results.push({
        type: 'knowledge',
        id: a.articleGuid,
        title: a.title || 'Untitled Article',
        subtitle: [a.blogType?.replace(/_/g, ' '), a.authorDisplayName].filter(Boolean).join(' · '),
        route: `/knowledge/${a.articleGuid}`,
        badge: a.blogType || undefined,
      });
      if (results.length >= MAX_PER_CATEGORY) break;
    }
  }
  return results;
}

// ── Type config ─────────────────────────────────────────────────
const TYPE_ORDER: SearchResultType[] = [
  'project',
  'mini_project',
  'community',
  'user',
  'chronicle',
  'knowledge',
];

const TYPE_ICONS: Record<SearchResultType, string> = {
  project: '📋',
  mini_project: '📝',  // distinct from project clipboard — "lighter weight" memo
  community: '🌍',
  user: '👤',
  chronicle: '📖',
  knowledge: '🔬',
};

// ── Component ───────────────────────────────────────────────────

export default function GlobalSearch({
  projects,
  visibleProjectGuids,
  miniProjects,
  communities,
  customEntities,
  relationships,
  users,
  currentUserGuid,
  communityMemberships,
  communityRoles,
  mode = 'bar',
}: GlobalSearchProps) {
  // Stable empty-array fallback for the new prop — mirrors the same
  // referential-stability pattern used for the other optional props.
  const miniProjectsArr = useMemo(() => miniProjects ?? [], [miniProjects]);

  // Look up community names by GUID once so each Mini-Project search
  // result can render "Category · Owner Community Name" without an
  // O(N×M) scan inside buildMiniProjectResults.
  const communityNameByGuid = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of communities) {
      const k = String(c.entity_guid || '').toUpperCase();
      if (k) map.set(k, c.name);
    }
    return map;
  }, [communities]);
  // DEV-946: stable empty-array fallbacks. Without useMemo, every render
  // would create new array references for any prop that came in undefined,
  // invalidating the deps of every downstream memo and re-running expensive
  // search builds on every keystroke.
  const customEntitiesArr = useMemo(() => customEntities ?? [], [customEntities]);
  const relationshipsArr = useMemo(() => relationships ?? [], [relationships]);
  const membershipsArr = useMemo(() => communityMemberships ?? [], [communityMemberships]);
  const rolesArr = useMemo(() => communityRoles ?? [], [communityRoles]);

  // DEV-890. Compute caller's visibility tier context once per render. Used
  // by buildCommunityResults to decide whether to admit private communities.
  const visibilityCtx = useMemo(() => {
    const callerNorm = String(currentUserGuid || '').toUpperCase();
    const me = users.find(
      (u: any) => String(u.user_guid || '').toUpperCase() === callerNorm,
    ) as any;
    const isSysAdmin = !!me?.is_system_admin;
    const memberOfGuids = new Set<string>();
    for (const m of membershipsArr) {
      if (String(m.user_guid || '').toUpperCase() !== callerNorm) continue;
      // Treat any membership status other than explicitly inactive as eligible.
      if (m.status === 'left' || m.status === 'banned') continue;
      memberOfGuids.add(String(m.community_entity_guid || '').toUpperCase());
    }
    const adminOfGuids = new Set<string>();
    for (const r of rolesArr) {
      if (String(r.user_guid || '').toUpperCase() !== callerNorm) continue;
      if (r.is_active === false) continue;
      if (r.role === 'admin' || r.role === 'owner') {
        adminOfGuids.add(String(r.community_entity_guid || '').toUpperCase());
      }
    }
    return { isSysAdmin, memberOfGuids, adminOfGuids };
     
  }, [currentUserGuid, users, membershipsArr, rolesArr]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // DEV-1389 — icon mode collapses the field to a magnifier that expands on
  // click / ⌘K. Bar mode keeps the field permanently open (the default).
  const iconMode = mode === 'icon';
  const [expanded, setExpanded] = useState(false);

  // ── Lazy-loaded content data (fetched once on first focus) ──
  const [chronicles, setChronicles] = useState<ChronicleSummary[]>([]);
  const [knowledgeArticles, setKnowledgeArticles] = useState<BlogArticleSummary[]>([]);
  const contentLoaded = useRef(false);

  const loadContentData = useCallback(async () => {
    if (contentLoaded.current) return;
    contentLoaded.current = true;
    try {
      const [chrResult, artResult] = await Promise.allSettled([
        fetchChronicles({ Status: 'published', PageSize: '200' }),
        // Fetch all published articles — use Status-only query (no BlogType filter)
        fetchArticlesByType('how_to', 1, 200),
      ]);
      if (chrResult.status === 'fulfilled') {
        const items = chrResult.value?.items;
        if (Array.isArray(items)) setChronicles(items);
      }
      if (artResult.status === 'fulfilled') {
        const articles = artResult.value?.articles;
        if (Array.isArray(articles)) setKnowledgeArticles(articles);
      }
    } catch { /* fail silently — search still works for other types */ }
  }, []);

  // ── Icon-mode expand / collapse ──
  const expand = useCallback(() => {
    setExpanded(true);
    setFocused(true);
    loadContentData();
    // Focus once the field has rendered.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [loadContentData]);

  const collapse = useCallback(() => {
    setExpanded(false);
    setFocused(false);
    setQuery('');
    inputRef.current?.blur();
  }, []);

  // ── Build search results (multi-term: "coral belize" matches items containing both) ──
  const grouped = useMemo(() => {
    if (query.trim().length < MIN_QUERY_LENGTH) return {} as Record<SearchResultType, SearchResult[]>;
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return {} as Record<SearchResultType, SearchResult[]>;
    return {
      project: buildProjectResults(projects, terms, visibleProjectGuids),
      mini_project: buildMiniProjectResults(miniProjectsArr, terms, communityNameByGuid),
      community: buildCommunityResults(communities, customEntitiesArr, relationshipsArr, terms, visibilityCtx),
      user: buildUserResults(users, terms, currentUserGuid),
      chronicle: buildChronicleResults(chronicles, terms),
      knowledge: buildKnowledgeResults(knowledgeArticles, terms),
    };
  }, [query, projects, visibleProjectGuids, miniProjectsArr, communityNameByGuid, communities, customEntitiesArr, relationshipsArr, users, chronicles, knowledgeArticles, currentUserGuid, visibilityCtx]);

  const flatResults = useMemo(
    () => TYPE_ORDER.flatMap((type) => grouped[type] || []),
    [grouped],
  );

  const totalCount = flatResults.length;
  const showDropdown = focused && query.trim().length >= MIN_QUERY_LENGTH;

  // ── Navigation ──
  const openResult = useCallback(
    (result: SearchResult) => {
      setQuery('');
      setFocused(false);
      inputRef.current?.blur();
      navigate(result.route);
    },
    [navigate],
  );

  // ── Keyboard handling ──
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIdx((prev) => (prev + 1) % Math.max(1, totalCount));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIdx((prev) => (prev - 1 + totalCount) % Math.max(1, totalCount));
      } else if (e.key === 'Enter' && totalCount > 0) {
        e.preventDefault();
        openResult(flatResults[highlightIdx]);
      } else if (e.key === 'Escape') {
        if (iconMode) {
          collapse();
        } else {
          setQuery('');
          setFocused(false);
          inputRef.current?.blur();
        }
      }
    },
    [totalCount, flatResults, highlightIdx, openResult, iconMode, collapse],
  );

  // ── Global ⌘K / Ctrl+K shortcut ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (iconMode) {
          expand();
        } else {
          inputRef.current?.focus();
          setFocused(true);
          loadContentData();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // DEV-946: loadContentData is referenced inside the keydown handler.
    // Safe to include because loadContentData itself is wrapped in
    // useCallback([]) and is stable across renders. DEV-1389: expand() is
    // likewise useCallback-stable.
  }, [loadContentData, iconMode, expand]);

  // ── Click-outside to close ──
  // Active when the dropdown is open OR (icon mode) the field is expanded with
  // no query yet — so an outside click collapses the field back to the icon.
  useEffect(() => {
    if (!showDropdown && !(iconMode && expanded)) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setFocused(false);
        if (iconMode) collapse();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown, iconMode, expanded, collapse]);

  // ── Scroll highlighted item into view ──
  useEffect(() => {
    if (!showDropdown || totalCount === 0) return;
    const el = dropdownRef.current?.querySelector(`[data-search-idx="${highlightIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx, showDropdown, totalCount]);

  // Reset highlight when query changes
  useEffect(() => { setHighlightIdx(0); }, [query]);

  // ── Type label translator ──
  const typeLabel = useCallback(
    (type: SearchResultType) => t(`common:search.type.${type}`),
    [t],
  );

  // ── Render ──
  let globalIdx = -1;

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${iconMode ? styles.wrapperIcon : ''} ${iconMode && expanded ? styles.wrapperExpanded : ''}`}
    >
      {/* DEV-1389 — icon mode shows a collapsed magnifier that expands on
          click / ⌘K; bar mode shows the persistent field (default). */}
      {iconMode && !expanded ? (
        <button
          type="button"
          className={styles.iconTrigger}
          onClick={expand}
          aria-label={t('common:search.placeholder')}
          title={t('common:search.placeholder')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
            <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
      <div className={`${styles.inputWrapper} ${focused ? styles.inputWrapperFocused : ''}`}>
        <svg className={styles.searchIcon} width="15" height="15" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
          <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { setFocused(true); loadContentData(); }}
          onKeyDown={handleKeyDown}
          placeholder={t('common:search.placeholder')}
          aria-label={t('common:search.placeholder')}
          autoComplete="off"
          spellCheck={false}
        />
        {query ? (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            aria-label={t('common:search.clear')}
          >
            ✕
          </button>
        ) : (
          <span className={styles.shortcut}>
            <kbd className={styles.kbd}>⌘</kbd>
            <kbd className={styles.kbd}>K</kbd>
          </span>
        )}
      </div>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div ref={dropdownRef} className={styles.dropdown} role="listbox">
          {totalCount === 0 ? (
            <div className={styles.emptyState}>
              {t('common:search.noResults', { query })}
            </div>
          ) : (
            <>
              {TYPE_ORDER.map((type) => {
                const items = grouped[type];
                if (!items || items.length === 0) return null;
                return (
                  <div key={type} className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.sectionIcon}>{TYPE_ICONS[type]}</span>
                      <span className={styles.sectionLabel}>{typeLabel(type)}</span>
                      <span className={styles.sectionCount}>
                        {items.length === MAX_PER_CATEGORY ? `${MAX_PER_CATEGORY}+` : items.length}
                      </span>
                    </div>
                    {items.map((result) => {
                      globalIdx++;
                      const idx = globalIdx;
                      const highlighted = idx === highlightIdx;
                      return (
                        <div
                          key={result.id}
                          data-search-idx={idx}
                          role="option"
                          aria-selected={highlighted}
                          className={`${styles.resultRow} ${highlighted ? styles.resultRowHighlighted : ''}`}
                          onMouseEnter={() => setHighlightIdx(idx)}
                          onClick={() => openResult(result)}
                        >
                          <ResultAvatar result={result} />
                          <div className={styles.resultText}>
                            <div className={styles.resultTitle}>{result.title}</div>
                            {result.subtitle && (
                              <div className={styles.resultSubtitle}>{result.subtitle}</div>
                            )}
                          </div>
                          {result.badge && (
                            <span className={`${styles.badge} ${styles[`badge_${result.type}`] || ''}`}>
                              {result.badge}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    <div className={styles.sectionDivider} />
                  </div>
                );
              })}
              <div className={styles.footer}>
                <span>↑↓ {t('common:search.navigate')} · ↵ {t('common:search.open')} · esc {t('common:search.close')}</span>
                <span>{totalCount} {t('common:search.results')}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── ResultAvatar sub-component ──────────────────────────────────

function ResultAvatar({ result }: { result: SearchResult }) {
  if (result.type === 'user') {
    if (result.image) {
      return <img src={mediaSrc(result.image) ?? undefined} alt="" className={styles.avatarImg} />;
    }
    const initials = result.title.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
    return <div className={styles.avatarInitials}>{initials}</div>;
  }
  if (result.type === 'community') {
    return <span className={styles.communityIcon}>🌍</span>;
  }
  const icon = TYPE_ICONS[result.type];
  return (
    <span className={`${styles.typeIcon} ${styles[`typeIcon_${result.type}`] || ''}`}>
      {icon}
    </span>
  );
}
